import React, { useState } from 'react';
import { Check, X, Loader2, Edit3, Send, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

const DraftCard = ({ artifact, onEditRequest }) => {
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [error, setError] = useState(null);

    const { title, summary, action } = artifact;

    const handleApprove = async () => {
        if (!action || status === 'loading' || status === 'success') return;

        setStatus('loading');
        setError(null);

        try {
            const config = {
                method: action.method || 'POST',
                url: `${API_BASE_URL}${action.endpoint}`,
                data: action.payload,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            };

            await axios(config);
            setStatus('success');
        } catch (err) {
            console.error('Draft Approval Error:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to execute action');
            setStatus('error');
        }
    };

    const handleEdit = () => {
        if (onEditRequest) {
            onEditRequest(`I want to modify the "${title}" proposal. Specifically...`);
        }
    };

    const isWatchlist = artifact.type?.includes('watchlist');
    const isAlert = artifact.type?.includes('alert');

    return (
        <div className={`mt-4 overflow-hidden rounded-2xl border-2 border-dashed transition-all duration-300 ${status === 'success'
                ? 'border-green-500 bg-green-50/30'
                : status === 'error'
                    ? 'border-red-500 bg-red-50/30'
                    : 'border-blue-200 bg-blue-50/30 hover:border-blue-300'
            }`}>
            {/* Header */}
            <div className={`px-4 py-3 flex items-center justify-between border-b ${status === 'success' ? 'border-green-100' : 'border-blue-100'
                }`}>
                <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${status === 'success' ? 'bg-green-500 text-white' : 'bg-blue-600 text-white'
                        }`}>
                        {status === 'success' ? <Check size={16} strokeWidth={3} /> : (isWatchlist ? <Edit3 size={16} /> : <AlertCircle size={16} />)}
                    </div>
                    <div>
                        <h4 className={`text-sm font-bold ${status === 'success' ? 'text-green-900' : 'text-blue-900'}`}>
                            {status === 'success' ? 'Task Completed' : title}
                        </h4>
                        <p className={`text-[10px] uppercase tracking-wider font-bold ${status === 'success' ? 'text-green-600' : 'text-blue-600'}`}>
                            {status === 'success' ? 'Draft Approved' : 'Draft Proposal'}
                        </p>
                    </div>
                </div>

                {status === 'idle' && (
                    <button
                        onClick={handleEdit}
                        className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-all"
                        title="Modify proposal"
                    >
                        <Edit3 size={18} />
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="p-4 space-y-3">
                <div className="space-y-1.5">
                    {summary.map((line, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-[13px] text-gray-700 leading-relaxed">
                            <span className="text-blue-400 mt-1">•</span>
                            <span>{line.replace('• ', '')}</span>
                        </div>
                    ))}
                </div>

                {error && (
                    <div className="p-3 bg-red-100 border border-red-200 rounded-xl flex items-start gap-2">
                        <X className="text-red-500 mt-0.5 shrink-0" size={14} />
                        <p className="text-[11px] text-red-700 font-medium leading-normal">{error}</p>
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="px-4 pb-4">
                {status === 'idle' || status === 'error' ? (
                    <button
                        onClick={handleApprove}
                        disabled={status === 'loading'}
                        className="w-full py-2.5 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                        {status === 'loading' ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <>
                                <Send size={14} />
                                Approve & Execute
                            </>
                        )}
                    </button>
                ) : status === 'success' ? (
                    <div className="w-full py-2.5 bg-green-500 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-2 shadow-inner">
                        <Check size={16} strokeWidth={3} />
                        Successfully Registered
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default DraftCard;
